import { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import axios from "axios";
import { useApiClient } from "@/hooks/useApiClient";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { PropertyDetailOptionModal } from "@/components/PropertyDetailOptionModal";
import { reserveV2Excel, fetchV2Excel, type V2ExcelExport } from "../api/v2";

export const V2_EXCEL_POLL_MS = 10000;
export const V2_EXCEL_AUTO_RESERVATION_POLL_MS = 30000;
const pending = (status?: V2ExcelExport["status"]): boolean =>
  status === "queued" || status === "running";
const failureMessages = {
  TIMEOUT: "作成が時間内に完了しませんでした。",
  ACCESS_CHANGED: "依頼者や閲覧権限が変わったため作成できませんでした。",
  SOURCE_CHANGED: "解析結果が変更されたため作成できませんでした。",
  EXCEL_LIMIT:
    "エクセルの行数・列数・セルの値の上限を超えたため作成できませんでした。",
  EXCEL_DETAIL_LIMIT: "明細数が5,000件の上限を超えたため作成できませんでした。",
  EXCEL_TIME_LIMIT: "作成時間の上限（15分）を超えたため作成できませんでした。",
  EXCEL_FILE_LIMIT:
    "エクセルのデータ量が上限を超えたため作成できませんでした。",
  GENERATION_FAILED: "エクセルの作成に失敗しました。",
};
const variants = {
  plain: {
    name: "共有エクセル",
    fileName: (requestId: number) => `全部事項解析結果_共有_${requestId}.xlsx`,
    notRequested:
      "解析が完了すると自動で作成されます。始まっていない場合はボタンから作成できます。",
  },
  // Same option, wording and file-name prefix as the v1 output menu.
  propertyDetail: {
    name: "共有エクセル（物件名称・物件住所付き）",
    fileName: (requestId: number) =>
      `【物件名称・物件住所付き】全部事項解析結果_共有_${requestId}.xlsx`,
    notRequested:
      "自動では作成されません。必要な場合はボタンから作成してください。",
  },
};

interface Props {
  requestId: number;
  // A separate job with its own state and polling; only users reserve it.
  includePropertyDetail?: boolean;
  isReadOnly?: boolean;
  isPropertyDetailOptionCredit?: boolean;
  onDownloadComplete?: () => void;
}

// This component is mounted only in the flag-on toolbar. URLs never enter state
// or a shared cache; every download obtains a fresh signed URL.
export const AllMattersV2Excel = ({
  requestId,
  includePropertyDetail = false,
  isReadOnly = false,
  isPropertyDetailOptionCredit = false,
  onDownloadComplete,
}: Props): JSX.Element => {
  const { apiClient } = useApiClient();
  const client = useRef(apiClient);
  client.current = apiClient;
  const controller = useRef<AbortController>();
  const inFlight = useRef(false);
  const autoDownload = useRef(false);
  const [data, setData] = useState<Omit<V2ExcelExport, "url">>();
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string>();
  const [forbidden, setForbidden] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const variant = includePropertyDetail
    ? variants.propertyDetail
    : variants.plain;
  // Matches v1: read-only users may only fetch an option file already paid for.
  const readOnlyBlocked =
    includePropertyDetail && isReadOnly && !isPropertyDetailOptionCredit;

  const refresh = async (create = false): Promise<void> => {
    const signal = controller.current?.signal;
    if (!signal || signal.aborted || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(undefined);
    setForbidden(false);
    try {
      // POST reserves only. GET is authoritative for both status and URL.
      if (create)
        await reserveV2Excel(
          client.current,
          requestId,
          signal,
          includePropertyDetail
        );
      let result = await fetchV2Excel(
        client.current,
        requestId,
        signal,
        includePropertyDetail
      );
      if (signal.aborted) return;
      // The property-detail status carries no URL. Only an explicit download
      // asks for one, because issuing the first one bills the option.
      if (
        includePropertyDetail &&
        result.status === "succeeded" &&
        autoDownload.current
      ) {
        result = await fetchV2Excel(
          client.current,
          requestId,
          signal,
          true,
          true
        );
        if (signal.aborted) return;
      }
      const { url, ...state } = result;
      setData(state);
      // Automatically reserved shared files become available without initiating a download.
      // Only an explicit button click opts this viewer into automatic download.
      if (result.status === "succeeded" && autoDownload.current) {
        if (!url || !/^https?:\/\//.test(url))
          throw new Error("Missing download URL");
        autoDownload.current = false;
        const link = document.createElement("a");
        link.href = url;
        link.download = variant.fileName(requestId);
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        link.remove();
        // Let the page reload its credit flag and skip the confirmation from then on.
        if (includePropertyDetail && !isPropertyDetailOptionCredit)
          onDownloadComplete?.();
      } else if (!pending(result.status)) {
        autoDownload.current = false;
      }
    } catch (cause) {
      if (signal.aborted || axios.isCancel(cause)) return;
      const status = axios.isAxiosError(cause)
        ? cause.response?.status
        : undefined;
      const denied = status === 401 || status === 403 || status === 404;
      setForbidden(denied);
      setError(
        denied
          ? `この依頼の${variant.name}を表示する権限がないか、依頼が見つかりません。`
          : `${variant.name}の作成依頼または状態の確認に失敗しました。時間をおいて「状態を再確認」を押してください。`
      );
    } finally {
      if (!signal.aborted) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  };

  const start = async (): Promise<void> => {
    autoDownload.current = true;
    await refresh(data?.status !== "succeeded");
  };

  useEffect(() => {
    controller.current = new AbortController();
    inFlight.current = false;
    void refresh();
    return () => {
      controller.current?.abort();
      autoDownload.current = false;
    };
  }, [requestId]);

  useEffect(() => {
    // Only the plain file is reserved automatically after analysis.
    const awaitingAutoReservation =
      !includePropertyDetail && data?.status === "not_requested";
    if ((!pending(data?.status) && !awaitingAutoReservation) || busy || error)
      return;
    const timer = window.setTimeout(
      () => {
        void refresh();
      },
      awaitingAutoReservation
        ? V2_EXCEL_AUTO_RESERVATION_POLL_MS
        : V2_EXCEL_POLL_MS
    );
    return () => {
      window.clearTimeout(timer);
    };
  }, [data, busy, error, requestId]);

  const waiting = pending(data?.status);
  const message =
    data?.status === "visibility_restricted"
      ? "一部の明細は現在閲覧できないため、共有ファイルをダウンロードできません。"
      : data?.status === "failed"
      ? failureMessages[data.error_code ?? "GENERATION_FAILED"]
      : data?.status === "expired"
      ? `${variant.name}の保存期限が切れました。もう一度作成してください。`
      : data?.status === "stale"
      ? "再解析などで解析結果が更新されました。最新の結果で作り直してください。"
      : undefined;
  const retry = data && ["failed", "expired", "stale"].includes(data.status);

  // One column at a fixed reading width: button, status and notice share the left edge.
  return (
    <Stack spacing={1} sx={{ minWidth: 0, width: "100%", maxWidth: 720 }}>
      <Box>
        <ReadOnlyTooltip isReadOnly={readOnlyBlocked}>
          <Button
            type="button"
            variant="outlined"
            sx={{ minHeight: 44 }}
            disabled={
              busy ||
              waiting ||
              Boolean(error) ||
              readOnlyBlocked ||
              data?.status === "visibility_restricted"
            }
            onClick={() => {
              // Like v1, confirm the option charge until this request has paid for it.
              if (includePropertyDetail && !isPropertyDetailOptionCredit)
                setConfirming(true);
              else void start();
            }}
          >
            {variant.name}
            {waiting
              ? "を作成中"
              : retry
              ? "を再作成"
              : data?.status === "not_requested"
              ? "を作成"
              : "をダウンロード"}
          </Button>
        </ReadOnlyTooltip>
      </Box>
      <Typography
        variant="body2"
        role="status"
        // An empty status would still take a gap in the column.
        sx={{ overflowWrap: "anywhere", "&:empty": { display: "none" } }}
      >
        {busy && !waiting
          ? "状態を確認しています。"
          : waiting
          ? `${
              data?.status === "queued"
                ? "作成を受け付けました。始まるまで数十秒〜数分かかり、完成までさらに時間がかかります。"
                : "作成しています。"
            }${
              autoDownload.current
                ? "この画面を開いたままにすると、完成後に自動でダウンロードします。"
                : "完成するとボタンからダウンロードできます。"
            }`
          : data?.status === "succeeded" && !error
          ? "作成済みです。ボタンからダウンロードできます。"
          : data?.status === "not_requested" && !error
          ? variant.notRequested
          : ""}
      </Typography>
      {(error ?? message) && (
        <Alert
          severity={forbidden ? "error" : "warning"}
          sx={{ overflowWrap: "anywhere" }}
        >
          {error ?? message}
        </Alert>
      )}
      {error && (
        <Box>
          <Button
            type="button"
            variant="outlined"
            disabled={busy}
            sx={{ minHeight: 44 }}
            onClick={() => {
              void refresh();
            }}
          >
            状態を再確認
          </Button>
        </Box>
      )}
      {includePropertyDetail && (
        <PropertyDetailOptionModal
          open={confirming}
          onClose={() => {
            setConfirming(false);
          }}
          onConfirm={start}
          target="all_matters"
        />
      )}
    </Stack>
  );
};
